/** @jsxImportSource @emotion/react */
import React from "react";
import * as Icons from "@goodgym/icons";
import * as UI from "@goodgym/components";
import * as T from "@goodgym/graphql/types";
import * as u from "@goodgym/util";
import _ from "lodash";
import AvailabilityForm from "./AvailabilityForm";
import { useRepeatHappening } from "@goodgym/graphql/mutations/RepeatSession";
import { useRepeatHappeningMutation } from "@goodgym/graphql/hooks";
export type AdminMenuProps = UI.IconButtonProps & {
  session: T.SessionFragment & { area?: Optional<T.AreaFragment> };
};

const AdminMenu: React.FC<AdminMenuProps> = ({ session, ...props }) => {
  const { data } = T.useSessionPageAdminMenuQuery({
    variables: { sessionId: session.id },
  });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const sessionTime = () => {
    const time = session.startedAt.match(/T(\d{2}):(\d{2})/);
    if (!time) return "";
    return time[1] + time[2];
  };

  const [showRepeatModal, setShowRepeatModal] = React.useState(false);
  const [availability, setAvailability] = React.useState([
    { date: "", time: sessionTime() },
  ]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [repeatHappening, { loading }] = useRepeatHappeningMutation({
    onCompleted: (data) => {
      const slug = data?.response?.sessions?.[0]?.slug;
      if (slug) {
        window.location.href = `/v3/session/${slug}`;
      } else {
        setShowRepeatModal(false);
      }
    },
    onError: () => setShowRepeatModal(false),
  });

  const handleRepeatMenuClick = () => {
    onClose();
    setShowRepeatModal(true);
  };

  const repeatHandler = useRepeatHappening()({
    session,
    date: availability[0].date,
    time: availability[0].time,
  });
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  };
  const onClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <UI.IconButton
        aria-label="admin menu icon"
        aria-controls={open ? "session-admin-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        size="small"
        {...props}
      >
        <Icons.Settings />
      </UI.IconButton>
      <UI.Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        <UI.MenuItem component={UI.Link} href={u.links.legacySession(session)}>
          <UI.ListItemIcon>
            <Icons.Visibility />
          </UI.ListItemIcon>{" "}
          View old page
        </UI.MenuItem>
        {data?.options?.map((option) => {
          return option.id === "repeat_session" && !option.url ? (
            <UI.MenuItem key={option.id} onClick={handleRepeatMenuClick}>
              <UI.ListItemIcon>
                <Icons.Repeat />
              </UI.ListItemIcon>
              {option.label}
            </UI.MenuItem>
          ) : (
            <UI.MenuOption key={option.id} option={option} onClose={onClose} />
          );
        })}

        <UI.MenuItem component={UI.Link} href={u.links.sessionDigest(session)}>
          <UI.ListItemIcon>
            <Icons.Feed />
          </UI.ListItemIcon>{" "}
          Digest
        </UI.MenuItem>
      </UI.Menu>
      <UI.Modal
        open={showRepeatModal}
        onClose={() => {
          setShowRepeatModal(false);
        }}
      >
        <UI.Box
          sx={{
            p: 3,
            bgcolor: "white.main",
            borderRadius: 3,
            boxShadow: 12,
            minWidth: 320,
            maxWidth: "95vw",
            mx: "auto",
            my: "20vh",
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <UI.IconButton
            aria-label="Close modal"
            onClick={() => setShowRepeatModal(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "grey.500",
              zIndex: 1,
            }}
          >
            <Icons.Close />
          </UI.IconButton>
          <UI.Typography variant="h6" sx={{ mb: 2 }}>
            Repeat session
          </UI.Typography>

          <AvailabilityForm
            index={0}
            availability={availability}
            setAvailability={setAvailability}
          />

          <UI.Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 3 }}
            disabled={loading}
            onClick={repeatHandler}
          >
            {loading ? "Repeating..." : "Repeat session"}
          </UI.Button>
        </UI.Box>
      </UI.Modal>
    </>
  );
};

export default AdminMenu;
